import React, { useEffect, useRef, useState, createContext, useContext } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { Source } from '../../lib/models/Chat';
import './ChatComponents.css';
import { renderAsync } from 'docx-preview';

// imports for fileViewer
import { Slide, Button, Dialog, AppBar, Toolbar, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TransitionProps } from '@mui/material/transitions';

// file viewer transition logic: slide to left
export const Transition = React.forwardRef(function Transition(
    props: TransitionProps & { children: React.ReactElement },
    ref: React.Ref<unknown>,
    ) {
    return <Slide direction="left" ref={ref} {...props} />;
});

// Citation File Viewer Contexts
interface urlContextType {
    url: string;
    setUrl: React.Dispatch<React.SetStateAction<string>>; 
}
export const urlContext = createContext<urlContextType>({
    url: '',           
    setUrl: () => {},  
});

interface highlightTextContextType {
    highlightText: string;
    setHighlightText: React.Dispatch<React.SetStateAction<string>>; 
}
export const highlightTextContext = createContext<highlightTextContextType>({
    highlightText: '',
    setHighlightText: () => {},
});

interface sourceContextType {
    source: Source | null;
    setSource: React.Dispatch<React.SetStateAction<Source | null>>;
}
export const sourceContext = createContext<sourceContextType>({
    source: null,
    setSource: () => {},
});

interface citationViewerOpenContextType {
    citationViewerOpen: boolean;
    setCitationViewerOpen: React.Dispatch<React.SetStateAction<boolean>>;
}
export const citationViewerOpenContext = createContext<citationViewerOpenContextType>({
    citationViewerOpen: false,
    setCitationViewerOpen: () => {},
});

// applies highlight on chunk text for each citation for docx
const applyHighlight = (root: HTMLElement, searchText: string) => {
    if (!searchText) return;

    const normalizedSearch = searchText
        .replace(/[\u00A0\s]+/g, ' ')
        .replace(/[\u201C\u201D]/g, '"')
        .trim();

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    let fullText = "";
    const nodes: { node: Text; start: number; end: number }[] = [];

    let currentNode: Node | null;
    while ((currentNode = walker.nextNode())) {
        const textNode = currentNode as Text;
        const start = fullText.length;
        fullText += textNode.textContent;
        const end = fullText.length;
        nodes.push({ node: textNode, start, end });
    }

    const matchIndex = fullText.toLowerCase().indexOf(normalizedSearch.toLowerCase());

    if (matchIndex !== -1) {
        const matchEnd = matchIndex + normalizedSearch.length;
        const range = document.createRange();
        let startNode: Text | null = null;
        let endNode: Text | null = null;
        let startOffset = 0;
        let endOffset = 0;

        for (const { node, start, end } of nodes) {
            if (!startNode && matchIndex >= start && matchIndex < end) {
                startNode = node;
                startOffset = matchIndex - start;
            }
            if (matchEnd > start && matchEnd <= end) {
                endNode = node;
                endOffset = matchEnd - start;
                break;
            }
        }

        if (startNode && endNode) {
            range.setStart(startNode, startOffset);
            range.setEnd(endNode, endOffset);

            const mark = document.createElement('mark');
            mark.className = "citation-highlight";
            mark.style.backgroundColor = "rgba(255, 215, 0, 0.4)";
            mark.style.borderRadius = "2px";
            
            mark.appendChild(range.extractContents());
            range.insertNode(mark);

            mark.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }
};

// DocxViewer: implements docx-viewer module to open up pdf/docx in tab
export const DocxViewer: React.FC<{ url: string; highlightText?: string; source: any }> = ({ url, highlightText, source }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const {citationViewerOpen, setCitationViewerOpen} = useContext(citationViewerOpenContext);

    // extract file type
    const extension = source?.location?.split('.').pop()?.toLowerCase();

    useEffect(() => {
        if (extension !== 'docx') {
            setLoading(false);
            return;
        }

        setLoading(true);
        setError(false);

        let isMounted = true;
        
        if (containerRef.current) {
            containerRef.current.innerHTML = "";
        }

        // needed for docx
        fetch(url)
            .then(response => {
                if (!response.ok) throw new Error("Network response was not ok");
                return response.arrayBuffer();
            })
            .then(buffer => {
                if (containerRef.current && isMounted) {
                    return renderAsync(buffer, containerRef.current, undefined, {
                        className: "docx",
                        inWrapper: true,
                    });
                }
            })
            .then(() => {
                if (highlightText && containerRef.current && isMounted) {
                    applyHighlight(containerRef.current, highlightText);
                }
                if (isMounted) setLoading(false);
            })
            .catch(err => {
                console.error("Error rendering docx:", err);
                if (isMounted) {
                    setError(true);
                    setLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [url, extension, highlightText]);

    const fileType = () => {
        if (!source?.presigned_url) {
            return null; 
        }
        
        // pdf rendering in file viewer
        if (extension === 'pdf') {
            return (
                <Box
                    sx={(theme) => ({
                        bgcolor: '#f5f5f5',
                        height: '100%',
                        [theme.breakpoints.down('sm')]: {
                            flex: 1,
                            minHeight: 0,
                            display: 'flex',
                        },
                    })}
                >
                    <Box
                        component="iframe"
                        src={source.presigned_url} 
                        sx={(theme) => ({
                            border: 'none',
                            width: '100%',
                            height: '100%',
                            [theme.breakpoints.down('sm')]: {
                                flex: 1,
                            },
                        })}
                        title="PDF Viewer"
                    />
                </Box>
            );
        }
    
        // docx rendering from url with highlighting
        if (extension === 'docx') {
            return (
                <Box
                    sx={(theme) => ({
                        height: '100%',
                        overflow: 'auto',
                        bgcolor: '#f5f5f5',
                        position: 'relative',
                        [theme.breakpoints.down('sm')]: {
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                        },
                    })}
                >
                    {loading && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', position: 'absolute', width: '100%', zIndex: 1, bgcolor: 'rgba(255,255,255,0.7)' }}>
                            <CircularProgress />
                        </Box>
                    )}
                    
                    {error && (
                        <Typography color="error" sx={{ p: 4, textAlign: 'center' }}>
                            Failed to load Word document.
                        </Typography>
                    )}
    
                    <Box
                        ref={containerRef}
                        sx={(theme) => ({
                            height: 'calc(100vh - 48px)',
                            [theme.breakpoints.down('sm')]: {
                                height: '100%',
                                overflowY: 'auto',
                                overflowX: 'hidden',
                                WebkitOverflowScrolling: 'touch',
                                touchAction: 'pan-y',
                                paddingLeft: theme.spacing(1),
                                paddingRight: theme.spacing(1),
                                paddingTop: theme.spacing(2),
                                paddingBottom: theme.spacing(2),
                                boxSizing: 'border-box',
                            },
                        })}
                    />
                </Box> 
            );
        }
    
        // fallback case until other file types are implemented
        return (
            <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6">File Format not Supported</Typography>
                <Typography variant="h6">Download to View</Typography>
            </Box>
        );
    }

    const handleCitationViewerClose = () => setCitationViewerOpen(false);

    return (
        <Dialog
            fullScreen
            open={citationViewerOpen}
            onClose={handleCitationViewerClose}
            TransitionComponent={Transition}
            scroll="paper"
            PaperProps={{
                sx: (theme) => ({
                    [theme.breakpoints.down('sm')]: {
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100dvh',
                        maxHeight: '100dvh',
                        overflow: 'hidden',
                    },
                }),
            }}
            >
            <AppBar
                sx={(theme) => ({
                    position: 'relative',
                    bgcolor: 'var(--color-accent, #1976d2)',
                    [theme.breakpoints.down('sm')]: {
                        position: 'sticky',
                        top: 0,
                    },
                })}
            >
                <Toolbar
                    sx={(theme) => ({
                        [theme.breakpoints.down('sm')]: {
                            gap: 1,
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            px: 1,
                        },
                    })}
                >
                    <IconButton edge="start" color="inherit" onClick={handleCitationViewerClose}>
                        <CloseIcon />
                    </IconButton>
                    <Typography
                        sx={(theme) => ({
                            ml: 2,
                            flex: 1,
                            [theme.breakpoints.down('sm')]: {
                                ml: 0,
                                minWidth: 0,
                                width: 'calc(100% - 56px)',
                                fontSize: '1rem',
                                overflow: 'hidden',
                                textOverflow: 'clip',
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                            },
                        })}
                        variant="h6"
                    >
                        {source?.name}
                    </Typography>
                    {source?.presigned_url && (
                        <Button
                            variant="outlined"
                            color="inherit"
                            component="a"
                            href={source.presigned_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={(theme) => ({
                                [theme.breakpoints.down('sm')]: {
                                    ml: 'auto',
                                    width: '100%',
                                },
                            })}
                        >
                            Download
                        </Button>
                    )}
                </Toolbar>
            </AppBar>
            <Box
                sx={(theme) => ({
                    height: 'calc(100vh - 48px)',
                    [theme.breakpoints.down('sm')]: {
                        flex: 1,
                        minHeight: 0,
                        display: 'flex',
                        overflow: 'hidden',
                    },
                })}
            >
                {fileType()}
            </Box>
        </Dialog>
    );
};